const parseIsoDateToUtc = (value?: string | null): number | null => {
  if (!value || typeof value !== "string") return null;
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(trimmed);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  return Date.UTC(year, month - 1, day);
};

const getCoverageRangeUtc = (statement: any = {}) => {
  const startDate = statement?.pay_period?.start_date;
  const endDate = statement?.pay_period?.end_date;
  const startUtc = parseIsoDateToUtc(startDate);
  const endUtc = parseIsoDateToUtc(endDate);
  if (startUtc === null || endUtc === null || endUtc < startUtc) return null;
  return { startUtc, endUtc };
};

const getCoverageBucketKey = (statement: any = {}) => {
  const startDate = statement?.pay_period?.start_date;
  const endDate = statement?.pay_period?.end_date;
  if (startDate && endDate) {
    return `period:${startDate}|${endDate}`;
  }
  return null;
};

export const calculatePlaidCoveredLookbackWeeks = (payrollStatements: any[] = []) => {
  if (!Array.isArray(payrollStatements) || payrollStatements.length === 0) return 0;
  const seenCoverageBuckets = new Set<string>();
  const coverageRanges: Array<{ startUtc: number; endUtc: number }> = [];

  payrollStatements.forEach((statement) => {
    const coverageBucket = getCoverageBucketKey(statement);
    if (coverageBucket && seenCoverageBuckets.has(coverageBucket)) return;
    if (coverageBucket) {
      seenCoverageBuckets.add(coverageBucket);
    }

    const range = getCoverageRangeUtc(statement);
    if (range) coverageRanges.push(range);
  });

  if (!coverageRanges.length) return 0;

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;
  coverageRanges.sort((a, b) => a.startUtc - b.startUtc);

  const merged: Array<{ startUtc: number; endUtc: number }> = [];
  for (const range of coverageRanges) {
    const last = merged[merged.length - 1];
    if (!last) {
      merged.push({ ...range });
      continue;
    }
    // Treat touching ranges as continuous coverage (avoid double-counting shared boundaries).
    if (range.startUtc <= last.endUtc + ONE_DAY_MS) {
      last.endUtc = Math.max(last.endUtc, range.endUtc);
      continue;
    }
    merged.push({ ...range });
  }

  const coveredDays = merged.reduce((sum, range) => {
    return sum + Math.floor((range.endUtc - range.startUtc) / ONE_DAY_MS) + 1;
  }, 0);
  if (!Number.isFinite(coveredDays) || coveredDays <= 0) return 0;
  return Math.round(coveredDays / 7);
};
